<template>
  <div class="grid-x flex-grow">
    <div class="cell auto cell-block-y flex flex-col flex-grow">
      <div class="flex flex-col flex-grow">
        <div class="flex items-center w-full justify-center min-h-90px bg-white"
          v-if="tab === 'promocodes'">
          <button
            class="cursor-pointer flex flex-row items-center"
            @click="onAddPromocodeClick">
            <span class="btn-plus mr-2"></span>
            <b class="text-15px leading-21px text-primary">Добавить промокод</b>
          </button>
        </div>
        <div class="flex-grow">
          <Loading :loading="waitPending">
            <Table
              :columns="columns"
              :rows="orders"
              :row-style-class="(row) => getRowStyleClass(row)"
              :busy="waitPending"
              :key="uid"
              @on-row-click="onRowClick"
              @on-sort-change="($event) => {
                $emit('update:order-by', $event);
              }"/>
          </Loading>
        </div>
        <div class="flex flex-row items-end justify-center p-6" v-if="page && pageCount">
          <Pagination
            :page="page"
            :page-count="pageCount"
            @change="onPageChange"/>
        </div>
        <Modal
          id="create-promocode"
          :fullScreen="true"
          :hide-footer="true"
          @close="onClose"
          :hideHeaderClose="false"
          >
          <template v-slot:modal-header-close>
            <img src="@/assets/icons/modal-header-close.svg" alt="modal-header-close" />
          </template>
          <template v-slot:modal-title>
            <div class="flex items-center w-full justify-between">
              <span class="text-xl">Создание нового промокода</span>
              <button @click="onCreatePromocodeClick"
              class="btn btn-sm btn-primary font-normal">Создать</button>
            </div>
          </template>
          <div slot class="flex flex-col pt-8">
            <div class="flex flex-col space-y-8 pl-48">
              <Input
                  label="Название промокода:"
                  placeholder="Введите название"
                  classes="bg-hint w-110"
                  :invalid="$v.promocode.name.$error"
                  v-model="promocode.name"/>
              <div class="max-w-300px">
                <span class="flex text-sm text-muted mb-4">Тип скидки:</span>
                <Select
                  :class="['bg-hint', {
                    'border border-solid border-hint':
                      !$v.promocode.type.$dirty || !$v.promocode.type.$invalid,
                    'border border-solid border-danger':
                      $v.promocode.type.$dirty && $v.promocode.type.$invalid,
                  }]"
                  :options="promocodeTypeOptions"
                  :label="'name'"
                  :value="promocode.type"
                  @input="($event) => promocode.type = $event"
                  placeholder="Выберите тип скидки"/>
              </div>
              <template v-if="promocode.type">
                <Input
                  v-if="promocode.type.value === 'fixed'"
                  label="Размер скидки в рублях"
                  placeholder="Введите величину скидки в рублях"
                  classes="w-110"
                  :invalid="$v.promocode.value.$error"
                  :inputmask="{
                    alias: 'decimal',
                    rightAlign: false,
                    allowMinus: false,
                    placeholder: '',
                  }"
                  v-model="promocode.value"/>
                <Input
                  v-else
                  label="Размер скидки в процентах"
                  placeholder="Введите величину скидки в процентах"
                  classes="w-110"
                  :invalid="$v.promocode.value.$error"
                  :inputmask="{
                    alias: 'numeric',
                    rightAlign: false,
                    allowMinus: false,
                    placeholder: '',
                    min: 0,
                    max: 100,
                    radixPoint: '',
                  }"
                  v-model="promocode.value"/>
              </template>
              <Input
                  label="Минимальная цена заказа:"
                  placeholder="Минимальная цена заказа, в рублях"
                  classes="w-150"
                  :inputmask="{
                    alias: 'decimal',
                    rightAlign: false,
                    allowMinus: false,
                    placeholder: '',
                  }"
                  :invalid="$v.promocode.minimalOrderPrice.$error"
                  v-model="promocode.minimalOrderPrice"/>
              <BaseDatePicker
                :title="'Выберите срок действия:'"
                v-model="promocode.expiresAt"
                @input="onSelectDate"
                :popover="{
                  placement: 'bottom-start',
                  visibility: 'click',
                }"
                trigger="click"
                placeholder="Выберите дату"
                :invalid="$v.promocode.expiresAt.$error"
                :mode="'single'"
              />
            </div>
          </div>
        </Modal>
        <Modal
          id="deactivatePromocode"
          :hide-footer="true"
          >
          <template v-slot:modal-title>
            <span class="modal-title">Деактивация промокода</span>
          </template>
          <div slot class="flex flex-col items-center p-8">
            <p class="text-center text-16px leading-22px mb-6 text-muted">
              Вы точно хотите деактивировать промокод
              <b v-if="promocode && promocode.name" class="text-primary">{{ promocode.name }}</b>?
            </p>
            <div class="flex items-center justify-center w-full">
              <span
                class="text-16px leading-18px text-danger font-bold cursor-pointer mr-10"
                @click="() => onDeactivateSend(this.promocode.id)">
                Деактивировать
              </span>
              <span
                class="text-16px leading-18px text-secondary font-bold cursor-pointer"
                @click="() => $bvModal.hide('deactivatePromocode')">
                Отмена
              </span>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  </div>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { required } from 'vuelidate/lib/validators';
import { mapActions, mapState } from 'vuex';
import Table from '@/components/Table.vue';
import Pagination from '@/components/Pagination.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import Attachment from '@/components/global/Attachment.vue';
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import BaseDatePicker from '@/components/BaseDatePicker.vue';
import ButtonBorderless from '@/components/ButtonBorderless.vue';
import Loading from '@/components/Loading.vue';
import OrderCountdown from '@/components/Order/OrderCountdown.vue';

export default {
  components: {
    Table,
    Pagination,
    Modal,
    Input,
    Select,
    BaseDatePicker,
    Loading,
  },
  props: {
    tab: {
      type: String,
      required: true,
    },
    statuses: {
      type: [Array],
      required: true,
    },
    orderBy: {
      type: [Array],
      required: true,
    },
    query: {
      type: String,
      required: false,
    },
    uid: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      limit: 100,
      page: 1,
      promocode: {
        name: null,
        value: null,
        minimalOrderPrice: null,
        type: null,
        expiresAt: null,
      },
      promocodeTypeOptions: [
        {
          id: 1,
          name: 'В рублях',
          value: 'fixed',
        },
        {
          id: 2,
          name: 'Процент от суммы',
          value: 'percentage',
        },
      ],
    };
  },
  validations: {
    promocode: {
      name: {
        required,
      },
      value: {
        required,
        isNumber: (value) => typeof +value === 'number',
      },
      minimalOrderPrice: {
        required,
      },
      type: {
        required,
      },
      expiresAt: {
        required,
      },
    },
  },
  computed: {
    ...mapState({
      payload: (state) => state.orders.data,
    }),
    columns() {
      switch (this.tab) {
        case 'active':
          return [
            {
              label: '№',
              field: 'id',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'firstServiceCategory.abbreviation') }-{ row.id }</b>
                <StatusBadge status={row.state}/>
                { row.isComplex && <span class="text-10px leading-12px font-bold text-primary">Комплексный</span> }
              </div>,
              sortable: true,
            },
            {
              label:
              'Создан',
              field: 'createdAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.createdAt) {
                  return null;
                }
                const date = this.$moment.utc(row.createdAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label:
              'Принят',
              field: 'takenByContractorAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.takenByContractorAt) {
                  return null;
                }
                const date = this.$moment.utc(row.takenByContractorAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label: 'Таймер',
              field: 'time_is_running_out_in',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.time_is_running_out_in?.expiresAt) {
                  return <span>&nbsp;</span>;
                }
                const diff = this.$moment(row.time_is_running_out_in.expiresAt, 'YYYY-MM-DD HH:mm:ss').diff(this.$moment());
                return (
                  <OrderCountdown diff={diff} />
                );
              },
            },
            // {
            //   label:
            //   'До завершения',
            //   field: 'closedAt',
            //   tdClass: 'align-middle',
            //   cell: ({ row }) => {
            //     const date = this.$moment(row.closedAt, 'YYYY-MM-DD HH:mm:ss');
            //     if (row.closedAt) {
            //       return (
            //         <div class="flex flex-col">
            //           <b>{ date.format('DD.MM.YYYY') }</b>
            //           <b>{ date.format('HH:mm:ss') }</b>
            //         </div>
            //       );
            //     }
            //     return (
            //       <div class="flex flex-col">
            //         <b>-</b>
            //       </div>
            //     );
            //   },
            //   sortable: false,
            // },
            {
              label: 'Стоимость',
              field: 'price',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.price)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Доход партнера',
              field: 'contractorPrice',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.contractorPrice)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Ком.',
              field: 'commissionPercent',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ row.commissionPercent ? `${row.commissionPercent}%` : '0%' }</b>,
              sortable: false,
            },
            {
              label: 'Заработок Iwaly',
              field: 'commissionAmount',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.commissionAmount)).format('0,0 $') }</b>,
              sortable: true,
            },
            { label: 'Локация', field: 'regionName', sortable: true },
            { label: 'Исполнитель', field: 'contractorName', sortable: true },
            {
              label: 'Метка партнера',
              field: 'partner',
              cell: ({ row }) => <span>{ _.get(row, 'partner.name') }</span>,
              sortable: true,
            },
            {
              label: 'Услуга',
              field: 'firstServiceCategoryId',
              cell: ({ row }) => {
                const id = _.get(row, 'firstServiceCategory.iconAttach');
                return <Attachment class="w-6 h-6 object-center object-contain" id={id}/>;
              },
              sortable: true,
            },
          ];
        case 'completed':
          return [
            {
              label: '№',
              field: 'id',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ row.name }</b>
                <StatusBadge status={row.state}/>
                { row.isComplex && <span class="text-10px leading-12px font-bold text-primary">Комплексный</span> }
              </div>,
              sortable: true,
            },
            {
              label:
              'Создан',
              field: 'createdAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.createdAt) {
                  return null;
                }
                const date = this.$moment.utc(row.createdAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label:
              'Принят',
              field: 'takenByContractorAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.takenByContractorAt) {
                  return null;
                }
                const date = this.$moment.utc(row.takenByContractorAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label:
              'Завершен',
              field: 'closedAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.closedAt) {
                  return null;
                }
                const date = this.$moment.utc(row.closedAt, 'YYYY-MM-DD HH:mm:ss').local();
                if (row.closedAt) {
                  return (
                    <div class="flex flex-col">
                      <b>{ date.format('DD.MM.YYYY') }</b>
                      <b>{ date.format('HH:mm:ss') }</b>
                    </div>
                  );
                }
                return (
                  <div class="flex flex-col">
                    <b>-</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label: 'Стоимость',
              field: 'price',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.price)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Доход партнера',
              field: 'contractorPrice',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.contractorPrice)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Ком.',
              field: 'commissionPercent',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ row.commissionPercent ? `${row.commissionPercent}%` : '0%' }</b>,
              sortable: false,
            },
            {
              label: 'Заработок Iwaly',
              field: 'commissionAmount',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.commissionAmount)).format('0,0 $') }</b>,
              sortable: true,
            },
            { label: 'Локация', field: 'regionName', sortable: true },
            { label: 'Исполнитель', field: 'contractorName', sortable: true },
            {
              label: 'Метка партнера',
              field: 'partner',
              cell: ({ row }) => <span>{ _.get(row, 'partner.name') }</span>,
              sortable: true,
            },
            {
              label: 'Услуга',
              field: 'firstServiceCategoryId',
              cell: ({ row }) => {
                const id = _.get(row, 'firstServiceCategory.iconAttach');
                return <Attachment class="w-6 h-6 object-center object-contain" id={id}/>;
              },
              sortable: true,
            },
            {
              label: 'Рейтинг',
              field: 'rating',
              cell: ({ row }) => {
                if (row.rating !== null) {
                  return <div class="flex flex-row items-center space-x-1">
                    {/* eslint-disable-next-line global-require */}
                    <img className="mb-1" src={require('@/assets/icons/star.svg')}/>
                    <b>{ this.$numeral(parseFloat(row.rating)).format('0.0') }</b>
                  </div>;
                }
                return null;
              },
              sortable: true,
            },
          ];
        case 'dispute':
          return [
            {
              label: '№',
              field: 'id',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'firstServiceCategory.abbreviation') }-{ row.id }</b>
                <StatusBadge status={row.state}/>
                { row.isComplex && <span class="text-10px leading-12px font-bold text-primary">Комплексный</span> }
              </div>,
              sortable: true,
            },
            {
              label:
              'Создан',
              field: 'createdAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.createdAt) {
                  return null;
                }
                const date = this.$moment.utc(row.createdAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label:
              'Принят',
              field: 'takenByContractorAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.takenByContractorAt) {
                  return null;
                }
                const date = this.$moment.utc(row.takenByContractorAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label: 'Спор открыт',
              field: 'disputeStartedAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.disputeStartedAt) {
                  return null;
                }
                const date = this.$moment.utc(row.disputeStartedAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: true,
            },
            {
              label: 'Стоимость',
              field: 'price',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.price)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Доход партнера',
              field: 'contractorPrice',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.contractorPrice)).format('0,0 $') }</b>,
              sortable: true,
            },
            {
              label: 'Ком.',
              field: 'commissionPercent',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ row.commissionPercent ? `${row.commissionPercent}%` : '0%' }</b>,
              sortable: false,
            },
            {
              label: 'Заработок Iwaly',
              field: 'commissionAmount',
              tdClass: 'align-middle',
              cell: ({ row }) => <b>{ this.$numeral(parseFloat(row.commissionAmount)).format('0,0 $') }</b>,
              sortable: true,
            },
            { label: 'Локация', field: 'regionName', sortable: true },
            { label: 'Исполнитель', field: 'contractorName', sortable: true },
            {
              label: 'Метка партнера',
              field: 'partner',
              cell: ({ row }) => <span>{ _.get(row, 'partner.name') }</span>,
              sortable: true,
            },
            {
              label: 'Услуга',
              field: 'firstServiceCategoryId',
              cell: ({ row }) => {
                const id = _.get(row, 'firstServiceCategory.iconAttach');
                return <Attachment class="w-6 h-6 object-center object-contain" id={id}/>;
              },
              sortable: true,
            },
            {
              label: '',
              field: ' ',
              cell: () => <div class="flex flex-row justify-end">
                <span class="arrow arrow-r"></span>
              </div>,
              sortable: false,
            },
          ];
        case 'promocodes':
          return [
            {
              label: 'ID',
              field: 'id',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'id') }</b>
              </div>,
              sortable: false,
            },
            {
              label: 'Название',
              field: 'name',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'name') }</b>
                <StatusBadge status={ row.active ? 'active' : 'cancelled' }/>
              </div>,
              sortable: false,
            },
            {
              label:
              'Срок действия',
              field: 'expiresAt',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (!row.expiresAt) {
                  return null;
                }
                const date = this.$moment.utc(row.expiresAt, 'YYYY-MM-DD HH:mm:ss').local();
                return (
                  <div class="flex flex-col">
                    <b>{ date.format('DD.MM.YYYY') }</b>
                    <b>{ date.format('HH:mm:ss') }</b>
                  </div>
                );
              },
              sortable: false,
            },
            {
              label: 'Сумма скидки по промокоду',
              field: 'ordersDiscountSum',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ this.$numeral(parseFloat(row.ordersDiscountSum)).format('0,0 $') }</b>
              </div>,
              sortable: true,
            },
            {
              label: 'Размер скидки',
              field: 'value',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'type') === 'fixed' ? this.$numeral(parseFloat(row.value)).format('0,0 $') : this.$numeral(parseFloat(row.value)).format('0%') }</b>
              </div>,
              sortable: false,
            },
            {
              label: 'Минимальная цена заказа',
              field: 'minimalOrderPrice',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ this.$numeral(parseFloat(row.minimalOrderPrice)).format('0,0 $') }</b>
              </div>,
              sortable: false,
            },
            {
              label: 'Количество использований',
              field: 'ordersCount',
              tdClass: 'align-middle',
              cell: ({ row }) => <div class="flex flex-col items-start">
                <b>{ _.get(row, 'ordersCount') }</b>
              </div>,
              sortable: false,
            },
            {
              label: '',
              field: 'deactivate',
              tdClass: 'align-middle',
              cell: ({ row }) => {
                if (row.active) {
                  return (
                    <div class="flex flex-col items-start">
                      <ButtonBorderless
                      onClick={(e) => { this.onDeactivateClick(e, row); }}>
                      Деактивировать</ButtonBorderless>
                    </div>
                  );
                }
                return null;
              },
              sortable: false,
            },
          ];
        default:
          return [];
      }
    },
    offset() {
      return (this.page - 1) * this.limit;
    },
    orders() {
      return _.get(this.payload, `${this.tab}.data`) || [];
    },
    total() {
      return _.get(this.payload, `${this.tab}.total`) || null;
    },
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    waitKey() {
      if (this.tab !== 'promocodes') {
        return `fetch.orders.${this.statuses.join(',')}`;
      }
      return 'fetch.orders.promocodes';
    },
    waitPending() {
      return this.$wait.is(this.waitKey);
    },
  },
  watch: {
    statuses: {
      // eslint-disable-next-line func-names, object-shorthand
      handler: function () {
        /** Выполним запрос на следующем тике после
         *  получения сортировки по умолчанию */
        this.$nextTick().then(() => {
          this.fetchPayload();
        });
      },
      deep: true,
      immediate: true,
    },
    'promocode.type': {
      handler(value) {
        this.$emit('input', value);
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions({
      fetch: 'orders/ACT_FETCH_ORDERS',
      createPromocode: 'orders/ACT_CREATE_PROMOCODE',
      deactivatePromocode: 'orders/ACT_DEACTIVATE_PROMOCODE',
    }),
    fetchPayload() {
      let { orderBy } = this;
      if (_.isEqual(orderBy, [])) {
        orderBy = [
          { column: 'id', descending: 1 },
        ];
      }
      this.fetch({
        key: this.tab,
        statuses: this.statuses,
        limit: this.limit,
        offset: this.offset,
        search: this.query,
        orderBy,
      });
    },
    onPageChange(event) {
      this.page = event;
    },
    onRowClick({ row: event, event: e }) {
      e.stopPropagation();
      if (this.tab !== 'promocodes') {
        this.$router.push({
          name: 'orders.info',
          params: {
            id: event.id,
          },
        });
      }
    },
    getRowStyleClass(row) {
      const classes = ['cursor-pointer'];
      if (row.priority) {
        classes.push('bg-primary-light bg-opacity-50');
      }
      return classes.join(' ');
    },
    onAddPromocodeClick() {
      this.clearPromocodeFields();
      this.resetValidation();
      this.$nextTick(() => {
        this.$bvModal.show('create-promocode');
      });
    },
    clearPromocodeFields() {
      this.promocode = {
        name: null,
        value: null,
        minimalOrderPrice: null,
        type: null,
        expiresAt: null,
      };
    },
    resetValidation() {
      this.$v.$reset();
    },
    onClose() {
      this.clearPromocodeFields();
      this.resetValidation();
    },
    async onCreatePromocodeClick() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const payload = {
          ...this.promocode,
          type: this.promocode.type.value,
        };
        if (payload.type === 'percentage') {
          payload.value = (+payload.value / 100).toString();
        }
        await this.createPromocode(payload).then((response) => {
          if (response.status === 200) {
            this.fetchPayload();
            this.clearPromocodeFields();
            this.resetValidation();
            this.$nextTick(() => {
              this.$bvModal.hide('create-promocode');
            });
          }
        }).catch((error) => {
          if (error.response.status === 500 && error.response.data.message === 'Discount cannot be greater than service commission priority.') {
            this.$iwalyNotify.error({
              text: 'Скидка по промокоду не может быть больше или равной комиссии сервиса.',
              duration: 5000,
            });
          }
        });
      }
    },
    onSelectDate(date) {
      if (date) {
        // eslint-disable-next-line no-underscore-dangle
        const expiresAt = this.$moment(date).tz('UTC', true)._d;
        this.promocode = {
          ...this.promocode,
          expiresAt,
        };
      }
    },
    async onDeactivateClick(e, { id, name }) {
      this.promocode = {
        ...this.promocode,
        name,
        id,
      };
      this.$nextTick(() => {
        this.$bvModal.show('deactivatePromocode');
      });
    },
    async onDeactivateSend(id) {
      await this.deactivatePromocode({
        id,
      }).then((response) => {
        if (response.status === 200) {
          this.$nextTick(() => {
            this.$bvModal.hide('deactivatePromocode');
          });
          this.fetchPayload();
        }
      }).catch((error) => {
        console.log(error);
      });
    },
  },
  mounted() {
    this.$watch((that) => [that.page, that.orderBy, that.query], () => {
      this.fetchPayload();
    });
  },
};
</script>
